import { useState, useEffect } from "react";
import {
  Container,
  Button,
  Card,
  TableContainer,
  Dialog,
  DialogTitle,
  Stack,
} from "@mui/material";
import { Helmet } from "react-helmet-async";
import CustomBreadcrumbs from "../../../components/custom-breadcrumbs";

import { Link as RouterLink } from "react-router-dom";
import { PATH_DASHBOARD } from "../../../routes/paths";
import { useSettingsContext } from "../../../components/settings";
import Iconify from "../../../components/iconify";
import ProductTableToolbar from "../../../sections/@dashboard/product/ProductTableToolbar";
import Scrollbar from "../../../components/scrollbar/Scrollbar";
import ProductList from "../../../sections/@dashboard/product/ProductList";
import ProductNewEditForm from "../../../sections/@dashboard/product/ProductAddEditForm";
import StockAdjustmentRequestsDialog from "../../../sections/@dashboard/stock/StockAdjustmentRequestsDialog";
import ProductImportDialog from "../../../sections/@dashboard/product/ProductImportDialog";
import { useAuthContext } from "../../../auth/useAuthContext";
import { BrandService } from "../../../api/brand.service";
import { SupplierService } from "../../../api/supplier.service";
import { Brands } from "../../../@types/brand.type";
import { Suppliers } from "../../../@types/supplier.type";

export default function ProductListPage() {
  const { themeStretch } = useSettingsContext();
  const { user } = useAuthContext();
  const [openAddProduct, setOpenAddProduct] = useState(false);
  const [openRequestsDialog, setOpenRequestsDialog] = useState(false);
  const [openImportDialog, setOpenImportDialog] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [filterName, setFilterName] = useState("");
  const [filterBrandId, setFilterBrandId] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [filterSupplierId, setFilterSupplierId] = useState("");

  const [brands, setBrands] = useState<Brands[]>([]);
  const [suppliers, setSuppliers] = useState<Suppliers[]>([]);

  const handleCloseModal = (refresh?: boolean) => {
    setOpenAddProduct(false);
    if (refresh) {
      setRefreshKey((prev) => prev + 1);
    }
  };

  const handleImportSuccess = () => {
    setRefreshKey((prev) => prev + 1);
  };

  const handleFilterName = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilterName(event.target.value);
  };

  const handleResetFilter = () => {
    setFilterName("");
    setFilterBrandId("");
    setFilterCategory("");
    setFilterSupplierId("");
  };

  useEffect(() => {
    const fetchMasterData = async () => {
      try {
        const [brandRes, supplierRes] = await Promise.all([
          new BrandService().getAllBrands(),
          new SupplierService().getAllSuppliers(),
        ]);
        setBrands((brandRes?.data || []) as Brands[]);
        setSuppliers((supplierRes?.data || []) as Suppliers[]);
      } catch (e) {
        // leave selects empty; user can still filter by name
        setBrands([]);
        setSuppliers([]);
      }
    };

    fetchMasterData();
  }, []);

  return (
    <>
      <Helmet>
        <title>Product</title>
      </Helmet>

      <Container maxWidth={themeStretch ? false : "xl"}>
        <CustomBreadcrumbs
          heading="Product"
          links={[
            {
              name: "Dashboard",
              href: PATH_DASHBOARD.root,
            },
            {
              name: "Product",
              href: PATH_DASHBOARD.product.list,
            },
          ]}
          action={
            <Stack direction="row" spacing={1}>
              {user?.roleId !== "admin" && (
                <Button
                  variant="outlined"
                  startIcon={<Iconify icon="eva:list-fill" />}
                  onClick={() => setOpenRequestsDialog(true)}
                >
                  My Requests
                </Button>
              )}
              <Button
                onClick={() => setOpenAddProduct(true)}
                variant="contained"
                startIcon={<Iconify icon="eva:plus-fill" />}
              >
                New Product
              </Button>
              <Button
                onClick={() => setOpenImportDialog(true)}
                variant="soft"
                startIcon={<Iconify icon="eva:cloud-upload-fill" />}
              >
                Import
              </Button>
            </Stack>
          }
        />

        <Card>
          <ProductTableToolbar
            filterName={filterName}
            onFilterName={handleFilterName}
            onResetFilter={handleResetFilter}
            filterBrandId={filterBrandId}
            onFilterBrandId={(e) => {
              setFilterBrandId(String(e.target.value || ""));
            }}
            filterCategory={filterCategory}
            onFilterCategory={(e) => {
              setFilterCategory(String(e.target.value || ""));
            }}
            filterSupplierId={filterSupplierId}
            onFilterSupplierId={(e) => {
              setFilterSupplierId(String(e.target.value || ""));
            }}
            brands={brands}
            suppliers={suppliers}
          />
          <TableContainer sx={{ position: "relative", overflow: "unset" }}>
            <Scrollbar>
              <ProductList
                key={refreshKey}
                filterName={filterName}
                filterBrandId={filterBrandId}
                filterCategory={filterCategory}
                filterSupplierId={filterSupplierId}
                brands={brands}
              />
            </Scrollbar>
          </TableContainer>
          <Dialog
            fullWidth
            maxWidth="md"
            open={openAddProduct}
            onClose={() => handleCloseModal()}
          >
            <DialogTitle>Add Product</DialogTitle>
            <ProductNewEditForm onCancel={handleCloseModal} />

            {/* <AddPatient
              Fields={showFields}
              currentPatient={currentPatient}
              isEdit={isEditBool}
              onCancel={handleCloseModalPatient}
              onSubmitForm={getAllData}
            /> */}
          </Dialog>
          <StockAdjustmentRequestsDialog
            open={openRequestsDialog}
            onClose={() => setOpenRequestsDialog(false)}
          />
          <ProductImportDialog
            open={openImportDialog}
            onClose={() => setOpenImportDialog(false)}
            onSuccess={handleImportSuccess}
          />
        </Card>
      </Container>
    </>
  );
}
